import Styles from './ArticlePostPreview.module.scss'
import { PostPreviewHeader } from './PostPreviewHeader/PostPreviewHeader'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import File from 'blaze-ui/icons/20/File'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { ChevronUp } from 'almanac-ui/icons'
import { ChevronDown } from 'blaze-ui/icons'
import cx from 'classnames'
import { PostPreviewBody } from './PostPreviewBody/PostPreviewBody'
import { ArticlePostPreviewActionTypes, maxFileSize, useArticlePostPreviewContext } from './ArticlePostPreviewContext'
import { observer } from 'mobx-react-lite'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MobilePostPreviewHeader } from './MobilePostPreviewHeader/MobilePostPreviewHeader'
import { MobilePostPreviewFooter } from './MobilePostPreviewFooter/MobilePostPreviewFooter'
import { CanView } from 'components/Can'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ArticleView, MediaObject, UserRole } from 'types'
import { useEffect, useRef, useState } from 'react'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useDropzone } from 'react-dropzone'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { DragAndDropZone } from './DragAndDropZone/DragAndDropZone'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FileSubscription } from 'screens/Finder/components/FileSubscription/FileSubscription'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { useOptionalDocStore } from 'stores/DocStore'
import { useCreateMediaObjectsWithoutPresigning } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { IntegrationReauthBanner } from 'components/IntegrationReauthBanner/IntegrationReauthBanner'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'

export const ArticlePostPreview = observer(() => {
  const {
    state: { expanded, channel, editing, integration, files, changingCopy },
    almanacFile,
    dispatch,
  } = useArticlePostPreviewContext()
  const handleToggle = () => {
    dispatch({ type: ArticlePostPreviewActionTypes.TOGGLE_EXPAND })
  }

  const docStore = useOptionalDocStore()

  const containerRef = useRef<HTMLDivElement>(null)
  const wasGeneratingSuggestion = useRef(false)
  const { mutateAsync: createMediaObjects } = useCreateMediaObjectsWithoutPresigning()

  const { isMobile } = useResponsiveDesign()
  const articleMeta = useCurrentArticleMeta()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()
  const uploadsProjectQuery = useUploadsProject(currentWorkspaceId)
  const uploadsProjectId = uploadsProjectQuery.data?.project?.id
  const [isDragOver, setIsDragOver] = useState(false)
  const { isChannelPostable, isChannelSocialPostable } = useIsChannelPostable()
  const { disconnectedIntegrations } = useIntegrationHeartbeatStatus()

  const isCurrentIntegrationDisconnected =
    integration && disconnectedIntegrations.some((disconnected) => disconnected.id === integration.id)
  const onUpload = (newFiles: MediaObject[]) => {
    const maxFilesCount = integration ? maxFileSize[integration.provider] : Infinity
    dispatch({
      type: ArticlePostPreviewActionTypes.SET_FILES,
      payload: [...files, ...newFiles].slice(0, maxFilesCount),
    })
  }
  const { uploadImageFiles } = useImportMediaFiles(false, async (files) => {
    const mediaObjects = await createMediaObjects(
      files.map((file) => ({ url: file.url as string, mime_type: file.mime_type })),
    )

    showToastMessage('All files were uploaded successfully!', 'success')
    onUpload(mediaObjects)
  })
  const onDrop = (acceptedFiles: File[]) => {
    setIsDragOver(false)
    uploadImageFiles(acceptedFiles, uploadsProjectId)
  }
  const { getRootProps } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
  })

  useEffect(() => {
    if (!docStore) return

    if (docStore?.isGeneratingSuggestion) {
      wasGeneratingSuggestion.current = true
    } else if (wasGeneratingSuggestion.current && !docStore.isGeneratingSuggestion) {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      wasGeneratingSuggestion.current = false // Reset after the scroll action
    }
  }, [docStore?.isGeneratingSuggestion, docStore])

  if (isMobile && articleMeta && !isChannelSocialPostable(channel as ChannelType)) {
    return (
      <CanView
        as={[UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
        inWorkspace={articleMeta.company_id}
        inViewMode={[ArticleView.Commenting, ArticleView.Reading]}
      >
        <MobilePostPreviewHeader />
      </CanView>
    )
  }

  let footer

  if (isChannelSocialPostable(channel as ChannelType)) {
    if (isMobile) {
      footer = changingCopy && !editing ? <MobilePostPreviewFooter /> : null
    } else {
      footer = (
        <div className={cx(Styles.footer, { [Styles.hide]: isDragOver })}>
          <div className={Styles.footerLeft}>
            <File color="var(--dark-80)" />
            <Heading level={5} color="var(--dark-80)">
              Brainstorm and Drafts
            </Heading>
          </div>
          <Button variant="tertiary" frontIcon={expanded ? ChevronUp : ChevronDown} onPress={handleToggle}>
            {expanded ? 'Hide' : 'Show'} preview
          </Button>
        </div>
      )
    }
  }

  return (
    <div className={Styles.container} {...getRootProps()} ref={containerRef}>
      <FileSubscription
        file={{
          id: almanacFile.id,
          type: AssetType.Article,
        }}
      />
      {isDragOver && <DragAndDropZone />}
      <div
        className={cx(Styles.preview, {
          [Styles.editing]: editing,
          [Styles.hide]: isDragOver,
          [Styles.changingCopy]: changingCopy,
        })}
      >
        {!isMobile &&
          isChannelPostable(channel) &&
          !(articleMeta?.main_layer.public && workspace?.role === UserRole.UNAUTHED) && <PostPreviewHeader />}
        {isCurrentIntegrationDisconnected && <IntegrationReauthBanner className={Styles.reauthBanner} />}
        {isChannelSocialPostable(channel) && (
          <div className={cx(Styles.body, { [Styles.expanded]: expanded })}>
            <PostPreviewBody />
          </div>
        )}
      </div>

      {footer}
    </div>
  )
})
